import { useOrganizationUsersContext } from 'contexts/OrganizationUsersContext';

export interface UserDisplayProps {
    /** Correlation id of the user to display. */
    correlationId?: string | null;
    /** Styling passed through to the rendered span. */
    className?: string;
    /** Text shown when the user can't be resolved (unknown, disabled, or removed from the org). */
    unknownLabel?: string;
}

/**
 * Renders an organization user's name from their correlation id, resolved client-side via
 * OrganizationUsersProvider. Falls back to `unknownLabel` when the user isn't in the org map
 * (disabled/departed/cross-org). While the org users are still loading it renders an empty
 * placeholder so it never flashes the fallback on first paint.
 */
const UserDisplay = ({ correlationId, className, unknownLabel = 'Unknown' }: UserDisplayProps) => {
    const { getUser, isLoading } = useOrganizationUsersContext();
    const user = getUser(correlationId);

    if (!user && isLoading) {
        return <span className={className} aria-busy="true" />;
    }

    return <span className={className}>{user?.name ?? unknownLabel}</span>;
};

export default UserDisplay;
